import '@ui5/webcomponents/dist/features/InputSuggestions.js';
import {
  BusyIndicator,
  BusyIndicatorSize,
  Input,
  InputDomRef,
  InputPropTypes,
  SuggestionItem,
  Ui5CustomEvent
} from '@ui5/webcomponents-react';
import { debounce } from 'lodash';
import { forwardRef, useRef, useState } from 'react';
import {
  MsGraphUser,
  useGetMsGraphUsersSearchByQueryQuery,
  useGetUserInfoQuery
} from 'generated/graphql';
import { InputSuggestionItemSelectEventDetail } from '@ui5/webcomponents/dist/Input';

interface MsalEmailInputSearchProps extends InputPropTypes {
  onEmailChange: (value: string) => void;
  customSuggestionItemSelector?: (userItem: MsGraphUser) => string;
}

type ComboBoxEvent = Ui5CustomEvent<InputDomRef, never>;

const EMAIL_INPUT_DEBOUNCE_SEARCH_TIME = 600;

export const MsalEmailInputSearch = forwardRef<InputDomRef, MsalEmailInputSearchProps>(
  ({ value, onEmailChange, customSuggestionItemSelector, className, ...props }, ref) => {
    const [{ data: userData, fetching: fetchingUserData }] = useGetUserInfoQuery();

    const [isDebounceRunning, setIsDebounceRunning] = useState<boolean>(true);

    const isInternalUser = () => {
      return userData?.user?.internal ?? false;
    };

    const [{ data, fetching }] = useGetMsGraphUsersSearchByQueryQuery({
      pause:
        !value || value.length < 3 || fetchingUserData || !isInternalUser() || isDebounceRunning,
      variables: {
        query: value || ''
      }
    });

    const debouncedOnEmailChange = useRef(
      debounce((value: boolean) => {
        setIsDebounceRunning(value);
      }, EMAIL_INPUT_DEBOUNCE_SEARCH_TIME)
    ).current;

    const handleOnEmailChange = (event: ComboBoxEvent) => {
      setIsDebounceRunning(false);
      onEmailChange(event.target.value);
    };

    const handleOnEmailInput = (event: ComboBoxEvent) => {
      const { value } = event.target;
      onEmailChange(value);
      setIsDebounceRunning(true);
      debouncedOnEmailChange.cancel();

      if (!value) {
        onEmailChange('');
        return;
      }

      debouncedOnEmailChange(false);
    };

    const handleOnSuggestionItemSelect = (
      event: Ui5CustomEvent<InputDomRef, InputSuggestionItemSelectEventDetail>
    ) => {
      event.preventDefault();

      if (customSuggestionItemSelector) {
        const user = data?.users?.find((user) => user.id === event.detail.item.id);
        const customSuggestionItem = customSuggestionItemSelector(user!);
        onEmailChange(customSuggestionItem);
        return;
      }

      onEmailChange(event.detail.item.additionalText!);
    };

    return (
      <BusyIndicator
        active={fetching || fetchingUserData}
        delay={0}
        size={BusyIndicatorSize.Small}
        className={className}
      >
        <Input
          ref={ref}
          data-testid="msal-email-input"
          value={value}
          className={className}
          onChange={handleOnEmailChange}
          onSuggestionItemSelect={handleOnSuggestionItemSelect}
          onInput={handleOnEmailInput}
          showSuggestions
          noTypeahead
          {...props}
        >
          {value &&
            data?.users?.map((user) => {
              const sapNumberTxt = user.sapNumber ? `(${user.sapNumber})` : '';

              return (
                <SuggestionItem
                  data-testid="msal-email-input-item"
                  id={user.id}
                  key={user.id}
                  text={`${user.displayName!} ${sapNumberTxt}`}
                  additionalText={user.userPrincipalName!}
                />
              );
            })}
        </Input>
      </BusyIndicator>
    );
  }
);
